import { ref } from 'vue'
import type { DomainSearchResult, ViteAppConfiguration } from '../types/config'

/**
 * Composable for domain search functionality
 * Handles API calls to WordPress REST API for domain availability checks
 */
export function useDomainSearch() {
  const isSearching = ref(false)
  const results = ref<DomainSearchResult[]>([])
  const error = ref<string | null>(null)
  const isLoadingMore = ref(false)

  /**
   * Search for domain availability across multiple TLDs
   * 
   * @param domain - Domain name to search (without TLD)
   * @param tlds - Array of top-level domains (e.g., ['.co.za', '.com', '.net'])
   * @param append - Whether to append results (for "Show more") or replace them
   */
  const search = async (domain: string, tlds: string[], append: boolean = false) => {
    // Set appropriate loading state
    if (append) {
      isLoadingMore.value = true
    } else {
      isSearching.value = true
      error.value = null
      results.value = []
    }

    try {
      // Get WordPress configuration from global
      const config = (window as any).conflatedDomainsConfig as ViteAppConfiguration

      if (!config || !config.restUrl || !config.nonce) {
        throw new Error('WordPress REST API configuration not found')
      }

      // Use bulk endpoint to check all TLDs in a single request
      const response = await fetch(`${config.restUrl}/${config.apiNamespace}/domain/check-bulk`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-WP-Nonce': config.nonce
        },
        body: JSON.stringify({
          domain: domain,
          tlds: tlds
        })
      })

      if (!response.ok) {
        console.error('API Error Response:', response.status, response.statusText)
        throw new Error(`API request failed: 500 Internal Server Error`)
      }

      const data = await response.json()

      // WordPress REST API wraps response in {success: true, data: {...}}
      const responseData = data.data || data

      // Transform response to DomainSearchResult format
      if (responseData.results && Array.isArray(responseData.results)) {
        console.log('[useDomainSearch] Raw API results:', responseData.results)

        const newResults = responseData.results.map((result: any) => {
          const mappedResult = {
            domain: result.domain,
            tld: result.tld,
            available: result.available || false,
            price: result.price,
            currency: result.currency || 'ZAR',
            error: result.error
          }

          console.log('[useDomainSearch] Mapped result for', result.domain, ':', mappedResult)
          console.log('[useDomainSearch] Price type:', typeof result.price, 'Value:', result.price)

          return mappedResult
        })

        console.log('[useDomainSearch] Final results:', newResults)

        // Append or replace results
        if (append) {
          console.log('[useDomainSearch] Appending results. Current count:', results.value.length, 'Adding:', newResults.length)
          results.value = [...results.value, ...newResults]
          console.log('[useDomainSearch] New total count:', results.value.length)
        } else {
          console.log('[useDomainSearch] Replacing results with', newResults.length, 'items')
          results.value = newResults
        }
      }

    } catch (e) {
      error.value = e instanceof Error ? e.message : 'An unknown error occurred'
      console.error('Domain search error:', e)
    } finally {
      isSearching.value = false
      isLoadingMore.value = false
    }
  }

  /**
   * Clear search results and errors
   */
  const clear = () => {
    results.value = []
    error.value = null
    isSearching.value = false
    isLoadingMore.value = false
  }

  return {
    isSearching,
    isLoadingMore,
    results,
    error,
    search,
    clear
  }
}
